// components/PDFCollaborativeViewer.tsx
import React, { useState, useEffect, useRef } from 'react';
import { Document, Page, pdfjs } from 'react-pdf';
import io, { Socket } from 'socket.io-client';
import { ZoomIn, ZoomOut, Download, Save } from 'lucide-react';
import { SOCKET_URL as SOCKET_URL_CONFIG } from '../config/api';

// Configuration du worker PDF.js
pdfjs.GlobalWorkerOptions.workerSrc = `//cdnjs.cloudflare.com/ajax/libs/pdf.js/${pdfjs.version}/pdf.worker.min.js`;

const SOCKET_URL = SOCKET_URL_CONFIG || window.location.origin;

interface PDFCollaborativeViewerProps {
  projectId: string;
  user: {
    _id: string;
    name: string;
  };
  pdfUrl: string;
  onSave?: () => void;
}

interface Annotation {
  id: string;
  userId: string;
  userName: string;
  type: 'highlight' | 'comment' | 'drawing';
  page: number;
  x: number;
  y: number;
  content?: string;
  color?: string;
}

const PDFCollaborativeViewer: React.FC<PDFCollaborativeViewerProps> = ({
  projectId,
  user,
  pdfUrl,
  onSave
}) => {
  const [numPages, setNumPages] = useState<number>(0);
  const [pageNumber, setPageNumber] = useState(1);
  const [scale, setScale] = useState(1.5);
  const [annotations, setAnnotations] = useState<Annotation[]>([]);
  const [loading, setLoading] = useState(true);
  const socketRef = useRef<Socket | null>(null);

  useEffect(() => {
    // Initialiser Socket.io pour les annotations
    const token = localStorage.getItem('token');
    const socket = io(SOCKET_URL, {
      auth: { token },
      transports: ['websocket', 'polling'],
    });
    socketRef.current = socket;

    const roomId = `pdf-collab-${projectId}`;

    socket.on('connect', () => {
      socket.emit('pdf-collab:join', { projectId, roomId });
    });

    socket.on('pdf-collab:annotation', (data: Annotation) => {
      setAnnotations(prev => {
        if (!prev.find(a => a.id === data.id)) {
          return [...prev, data];
        }
        return prev;
      });
    });

    socket.on('pdf-collab:annotation-removed', (data: { id: string }) => {
      setAnnotations(prev => prev.filter(a => a.id !== data.id));
    });

    return () => {
      if (socketRef.current) {
        socketRef.current.emit('pdf-collab:leave', { projectId, roomId });
        socketRef.current.disconnect();
      }
    };
  }, [projectId]);

  const onDocumentLoadSuccess = ({ numPages }: { numPages: number }) => {
    setNumPages(numPages);
    setLoading(false);
  };

  const addAnnotation = (type: 'highlight' | 'comment', page: number, x: number, y: number) => {
    const annotation: Annotation = {
      id: `ann-${Date.now()}-${Math.random()}`,
      userId: user._id,
      userName: user.name,
      type,
      page,
      x,
      y,
      color: type === 'highlight' ? '#ffeb3b' : undefined,
    };

    setAnnotations(prev => [...prev, annotation]);

    if (socketRef.current && socketRef.current.connected) {
      socketRef.current.emit('pdf-collab:annotation', {
        projectId,
        annotation,
      });
    }
  };

  return (
    <div className="flex flex-col h-full bg-gray-100">
      {/* Toolbar */}
      <div className="flex items-center justify-between p-3 bg-white border-b border-gray-200">
        <div className="flex items-center gap-2">
          <button
            onClick={() => setPageNumber(prev => Math.max(1, prev - 1))}
            disabled={pageNumber <= 1}
            className="px-3 py-1 bg-gray-100 rounded hover:bg-gray-200 disabled:opacity-50"
          >
            Précédent
          </button>
          <span className="text-sm text-gray-600">
            Page {pageNumber} sur {numPages}
          </span>
          <button
            onClick={() => setPageNumber(prev => Math.min(numPages, prev + 1))}
            disabled={pageNumber >= numPages}
            className="px-3 py-1 bg-gray-100 rounded hover:bg-gray-200 disabled:opacity-50"
          >
            Suivant
          </button>
        </div>

        <div className="flex items-center gap-2">
          <button
            onClick={() => setScale(prev => Math.max(0.5, prev - 0.25))}
            className="p-2 hover:bg-gray-100 rounded"
            title="Zoom arrière"
          >
            <ZoomOut className="h-4 w-4" />
          </button>
          <span className="text-sm text-gray-600 w-16 text-center">
            {Math.round(scale * 100)}%
          </span>
          <button
            onClick={() => setScale(prev => Math.min(3, prev + 0.25))}
            className="p-2 hover:bg-gray-100 rounded"
            title="Zoom avant"
          >
            <ZoomIn className="h-4 w-4" />
          </button>
        </div>
      </div>

      {/* PDF Viewer */}
      <div className="flex-1 overflow-auto p-4 flex justify-center">
        {loading ? (
          <div className="flex items-center justify-center h-full">
            <p className="text-gray-600">Chargement du PDF...</p>
          </div>
        ) : (
          <div className="relative">
            <Document
              file={pdfUrl}
              onLoadSuccess={onDocumentLoadSuccess}
              loading={<div>Chargement...</div>}
            >
              <Page
                pageNumber={pageNumber}
                scale={scale}
                renderTextLayer={true}
                renderAnnotationLayer={true}
              />
            </Document>

            {/* Annotations overlay */}
            {annotations
              .filter(ann => ann.page === pageNumber)
              .map(annotation => (
                <div
                  key={annotation.id}
                  className="absolute"
                  style={{
                    left: `${annotation.x}px`,
                    top: `${annotation.y}px`,
                    backgroundColor: annotation.color || 'transparent',
                    padding: '2px 4px',
                    borderRadius: '2px',
                    fontSize: '12px',
                  }}
                  title={annotation.userName}
                >
                  {annotation.content}
                </div>
              ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default PDFCollaborativeViewer;

